/**
 * The one list of bearer-shaped credentials that can appear in a URL, shared by the Node
 * server's access log (server/logRedaction.js) and the browser's Sentry scrubber
 * (lib/sentryScrub.ts). It is plain ESM with no Node or DOM imports so both can load it.
 *
 * Mirrors the backend's `Shared/Core/src/Logging/LogRedaction.cs`; a new token-bearing route or
 * token prefix belongs in both. nginx.conf cannot import this, so it carries the same shapes in
 * its two `map` blocks (deploy-config.test.ts runs those regexes against these samples).
 */

/** Substituted for the value of a credential-shaped query parameter or path token. */
export const PLACEHOLDER = '[redacted]'

/** Share tokens are `shr_` + 24 base62 chars (ShareController.GenerateShareToken). */
const SHARE_TOKEN = /shr_[A-Za-z0-9]{8,}/g

/** The same shape, but as the whole of a query-parameter value. */
const SHARE_TOKEN_EXACT = /^shr_[A-Za-z0-9]{8,}$/

/**
 * Report download tokens are `rpt_` + 32 base64url chars (24 random bytes), minted by
 * ReportDownloads and served as the SPA route `/report-download/:token`. The body alphabet
 * includes `-` and `_`, unlike a share token's.
 */
const REPORT_TOKEN = /rpt_[A-Za-z0-9_-]{8,}/g

/**
 * A whole path segment of exactly 43 url-safe chars: the output of the backend's
 * `Crypto.GenerateUrlSafeToken()` (32 random bytes as padding-trimmed base64url).
 * That is what a password-reset or invitation token looks like, and this app serves them as SPA
 * routes — `/reset-password/:token`, `/invite/accept/:token` and the group invitation
 * `/invite/group/:region/:token` (App.tsx) — so the live credential is in the PATH, where a
 * query-only scrubber would never see it. The backend anchors on a list of route segments to
 * avoid false positives on its own identifiers; the SPA's URLs carry no other 43-char segment
 * (a GUID is 36, a share token 28, a report token 36), so matching the shape alone covers every
 * one of those routes, including the `/invitations/:token/join` form, without a list to forget
 * to extend. The negative lookahead stops a longer segment matching on its first 43 chars.
 */
const URL_TOKEN_SEGMENT = /(?<=\/)[A-Za-z0-9_-]{43}(?![A-Za-z0-9_-])/g

/**
 * Anchored on `?`/`&` so only a real parameter matches — `clinicCode=` is not
 * `code=`. Longer names precede their prefixes so alternation picks the specific one.
 */
const SENSITIVE_PARAM =
  /(?<=[?&])(access_token|refresh_token|id_token|client_secret|api_key|apikey|token|secret|signature|password|code)=[^&#]*/gi

/**
 * Returns `url` with any bearer-shaped credential replaced. Non-sensitive parameters are
 * preserved so the line stays useful for debugging.
 *
 * @param {string | undefined | null} url
 * @param {(token: string) => string} [redactCorrelated] how a share or report token is
 *   rendered. The server passes a stable hash so repeat hits correlate; the default is a
 *   constant, which is all a browser-side event needs.
 * @returns {string} the redacted URL, or '' when there was nothing
 */
export function redactUrlTokens(url, redactCorrelated = defaultCorrelated) {
  if (!url) return ''
  return (
    url
      // Named parameters first: a share token here must keep its correlatable
      // placeholder rather than be flattened to the generic one.
      .replace(SENSITIVE_PARAM, (param) => {
        const eq = param.indexOf('=')
        const value = param.slice(eq + 1)
        const replacement = isCorrelatedToken(value) ? redactCorrelated(value) : PLACEHOLDER
        return `${param.slice(0, eq + 1)}${replacement}`
      })
      // Then anywhere else a share or report token can appear — a path segment, or a
      // parameter whose name is not on the list above.
      .replace(SHARE_TOKEN, redactCorrelated)
      .replace(REPORT_TOKEN, redactCorrelated)
      // Finally the unprefixed single-use tokens, which are only ever path segments.
      .replace(URL_TOKEN_SEGMENT, PLACEHOLDER)
  )
}

/** `shr_…` or `rpt_…` as the whole of a value. */
function isCorrelatedToken(value) {
  return SHARE_TOKEN_EXACT.test(value) || /^rpt_[A-Za-z0-9_-]{8,}$/.test(value)
}

/** @param {string} token */
function defaultCorrelated(token) {
  return `${token.slice(0, 4)}[redacted]`
}
